/*
 * A labelled form control. The reason it is a component and not a convention: the label, the hint
 * and the error have to be wired to the control by id, and doing that by hand is where accessible
 * forms break. `Field` owns the ids; the caller renders the control through `children` and spreads
 * what it is handed.
 *
 * What it hands over includes the control's class. A native `<select>` or `<input>` that only
 * spreads the props is therefore already a `.tb-field__control` — the five unstyled controls the
 * profile page once had cannot come back — and a caller with a class of its own merges it:
 * `className={cx(control.className, "tb-auth__input")}`.
 */

import { useId, type ReactNode } from "react"
import "./ui.css"

/** The class every control inside a field wears. */
export const FIELD_CONTROL_CLASS = "tb-field__control"

export interface FieldControlProps {
	id: string
	className: string
	"aria-describedby": string | undefined
	"aria-invalid": boolean | undefined
}

interface FieldProps {
	/** Already translated. */
	label: string
	hint?: string
	/** Already translated; its presence marks the control invalid. */
	error?: string
	/** The field takes the whole row instead of its 24 rem reading width — a topic, a long text. */
	wide?: boolean
	children: (props: FieldControlProps) => ReactNode
}

export function Field({ label, hint, error, wide = false, children }: FieldProps) {
	const id = useId()
	const hintId = hint === undefined ? undefined : `${id}-hint`
	const errorId = error === undefined ? undefined : `${id}-error`
	const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined

	return (
		<div className={wide ? "tb-field tb-field--wide" : "tb-field"}>
			<label className="tb-field__label" htmlFor={id}>
				{label}
			</label>
			{children({
				id,
				className: FIELD_CONTROL_CLASS,
				"aria-describedby": describedBy,
				"aria-invalid": error === undefined ? undefined : true,
			})}
			{hint === undefined ? null : (
				<span className="tb-field__hint" id={hintId}>
					{hint}
				</span>
			)}
			{error === undefined ? null : (
				<span className="tb-field__error" id={errorId} role="alert">
					{error}
				</span>
			)}
		</div>
	)
}
